import { alpha, Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { FileX, Image, X } from 'lucide-react';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { CheckboxWithController } from '~components/hookForm/CheckboxWithController';
import { Loader } from '~components/reuseable/Loader';
import { AttachedImagesDeletionFormField } from '~modules/attachedFiles/forms/AttachedImagesDeletionForm';
import { UploadedFileModel } from '~modules/attachedFiles/models/UploadedFileModel';
import { UploadingStateEnum } from '~modules/attachedFiles/types/UploadingStateEnum';
import { useCommonStyles } from '~styles/commonStyles';

interface IFilePreviewItemProps {
  file: UploadedFileModel;
  onDelete: (file: UploadedFileModel) => void;
  readonly?: boolean;
  onOpenGallery: () => void;
}

export const ImagePreview: React.FC<IFilePreviewItemProps> = memo(
  (props) => {
    const { file, onDelete, readonly, onOpenGallery } = props;

    const [imageLoading, setImageLoading] = useState(true);

    const { t } = useTranslation('common');
    const { classes, cx } = useStyles();
    const { classes: commonClasses } = useCommonStyles();
    const { palette } = useTheme();

    // Handlers

    const handleDeleteFile = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      onDelete(file);
    };

    const handleImageLoad = () => {
      setImageLoading(false);
    };

    const handleImageError = () => {
      setImageLoading(false);
    };

    useEffect(() => {
      setImageLoading(true);
    }, [file.previewUrl]);

    // Render

    const renderDeletionCheckBox = () => {
      if (!file?.fileId || readonly) {
        return;
      }

      return (
        <div className={classes.deleteFileCheckbox}>
          <CheckboxWithController
            name={`${AttachedImagesDeletionFormField.imagesToDelete}.${file.fileId}`}
            // checkboxProps={{ size: 'small' }}
          />
        </div>
      );
    };

    const renderSuccess = () => {
      if (!file.previewUrl) {
        return (
          <Box className={cx(commonClasses.fileItem, classes.withoutPreview)} onClick={onOpenGallery}>
            <Stack alignItems="center" spacing={1}>
              <Image size={24} />
              <Typography className={classes.openImageText}>{t('openImage')}</Typography>
            </Stack>
            {renderDeletionCheckBox()}
          </Box>
        );
      }

      return (
        <Box className={cx(commonClasses.fileItem, classes.filePreview)} onClick={onOpenGallery}>
          <img
            src={`${import.meta.env.VITE_APP_API_URL}${file.previewUrl}`}
            alt="image"
            className={classes.previewImage}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
          {imageLoading && (
            <Box className={classes.loadingOverlay}>
              <Loader size={24} />
            </Box>
          )}
          {renderDeletionCheckBox()}
        </Box>
      );
    };

    const renderLoading = () => {
      // Если файл загружается впервые (нет previewUrl), показываем простой лоадер
      if (!file.previewUrl) {
        return (
          <Box className={cx(commonClasses.fileItem, classes.fileLoading)}>
            <Loader size={24} />
          </Box>
        );
      }

      // Если файл удаляется (есть previewUrl), показываем лоадер поверх превью
      return (
        <Box className={cx(commonClasses.fileItem, classes.filePreview)}>
          <img
            src={`${import.meta.env.VITE_APP_API_URL}${file.previewUrl}`}
            alt="image"
            className={classes.previewImage}
          />
          <Box className={classes.loadingOverlay}>
            <Loader size={24} />
          </Box>
        </Box>
      );
    };

    const renderError = () => {
      return (
        <Box className={cx(commonClasses.fileItem, classes.fileError)}>
          <FileX size={24} color={palette.error.main} />
          <IconButton size="small" className={classes.deleteFileButton} onClick={handleDeleteFile}>
            <X size={15} />
          </IconButton>
        </Box>
      );
    };

    const fileVariantsMap = {
      [UploadingStateEnum.LOADING]: renderLoading(),
      [UploadingStateEnum.SUCCESS]: renderSuccess(),
      [UploadingStateEnum.ERROR]: renderError(),
    };

    return fileVariantsMap[file.uploadingState];
  },
  (prevProps, nextProps) => {
    // Компонент перерендерится только если изменился сам файл или режим отображения
    return (
      prevProps.file.fileId === nextProps.file.fileId &&
      prevProps.file.uploadingState === nextProps.file.uploadingState &&
      prevProps.file.previewUrl === nextProps.file.previewUrl &&
      prevProps.file.documentUrl === nextProps.file.documentUrl &&
      prevProps.readonly === nextProps.readonly
    );
  },
);

const useStyles = makeStyles()((theme) => ({
  withoutPreview: {
    border: `1px solid ${alpha(theme.palette.primary.main, 1)}`,
    '& .lucide': {
      color: alpha(theme.palette.primary.main, 1),
      transition: theme.transitions.create('color'),
    },
    '&:hover': {
      borderColor: alpha(theme.palette.primary.main, 1),
      '& .lucide': {
        color: alpha(theme.palette.primary.main, 1),
      },
    },
  },
  fileLoading: {
    cursor: 'default',
  },
  filePreview: {
    border: 'none',
    padding: 0,
  },
  fileError: {
    border: `2px dashed ${alpha(theme.palette.error.main, 0.5)}`,
    '&:hover': {
      borderColor: alpha(theme.palette.error.main, 1),
    },
    cursor: 'default',
  },
  deleteFileButton: {
    position: 'absolute',
    top: theme.spacing(-1),
    right: theme.spacing(-1),
    backgroundColor: alpha(theme.palette.common.white, 0.5),
    boxShadow:
      '0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
    '&:hover': {
      backgroundColor: theme.palette.common.white,
    },
  },
  deleteFileCheckbox: {
    position: 'absolute',
    top: 3,
    right: 3,
    '.MuiCheckbox-root:hover': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    borderRadius: theme.spacing(1),
  },
  openImageText: {
    textAlign: 'center',
    color: theme.palette.primary.main,
    fontWeight: 500,
    fontSize: 13,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(theme.palette.common.white, 0.8),
    borderRadius: theme.spacing(1),
  },
}));
